import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  GripVertical,
  HeartHandshake,
  Image as ImageIcon,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import { useAuthStore } from "../context/AuthContext";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { hasPermission } from "../lib/utils/permissions";
import { resolvePartnerLogoUrl } from "../lib/utils/partnerAssets";
import useDragAutoScroll from "../hooks/useDragAutoScroll";

const GROUPS = [
  { id: "all", label: "All Partners", icon: HeartHandshake },
  { id: "corporate", label: "Corporate & Institutional", icon: Building2 },
  { id: "insurance", label: "Insurance Companies", icon: ShieldCheck },
  { id: "tpa", label: "TPA Partners", icon: HeartHandshake },
];

const TYPE_LABELS = {
  corporate: "Corporate & Institutional",
  insurance: "Insurance Company",
  tpa: "TPA Partner",
};

const Partners = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const canList = hasPermission(user, "partners", "list");
  const canCreate = hasPermission(user, "partners", "create");
  const canEdit = hasPermission(user, "partners", "edit");
  const canDelete = hasPermission(user, "partners", "delete");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedId),
    !isReordering,
  );

  const fetchPartners = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/partners/admin");
      setItems(response.data?.data?.partners || []);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to load partners");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canList) fetchPartners();
  }, [canList]);

  const counts = useMemo(
    () => ({
      all: items.length,
      corporate: items.filter((item) => item.partner_type === "corporate").length,
      insurance: items.filter((item) => item.partner_type === "insurance").length,
      tpa: items.filter((item) => item.partner_type === "tpa").length,
    }),
    [items],
  );

  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    return items.filter((item) => {
      if (activeGroup !== "all" && item.partner_type !== activeGroup) return false;
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      return !search || `${item.name} ${item.category || ""}`.toLowerCase().includes(search);
    });
  }, [activeGroup, items, query, statusFilter]);

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/partners/${item.id}`);
      showSuccessToast("Partner deleted successfully");
      fetchPartners();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete partner");
    }
  };

  const resetDragState = () => {
    stopDragAutoScroll();
    setDraggedId(null);
    setDropTargetId(null);
  };

  const handleDrop = async (event, targetItem) => {
    event.preventDefault();
    stopDragAutoScroll();

    const draggedItem = items.find((item) => item.id === draggedId);
    if (!draggedItem || draggedItem.id === targetItem.id) {
      resetDragState();
      return;
    }
    if (draggedItem.partner_type !== targetItem.partner_type) {
      showErrorToast("Partners can only be reordered within the same group");
      resetDragState();
      return;
    }

    const groupItems = items.filter(
      (item) => item.partner_type === draggedItem.partner_type,
    );
    const draggedIndex = groupItems.findIndex((item) => item.id === draggedItem.id);
    const targetIndex = groupItems.findIndex((item) => item.id === targetItem.id);
    if (draggedIndex < 0 || targetIndex < 0) {
      resetDragState();
      return;
    }

    const reorderedGroup = [...groupItems];
    const [movedItem] = reorderedGroup.splice(draggedIndex, 1);
    reorderedGroup.splice(targetIndex, 0, movedItem);
    const orderById = new Map(
      reorderedGroup.map((item, index) => [item.id, index + 1]),
    );
    const typeOrder = { corporate: 0, insurance: 1, tpa: 2 };
    const reorderedItems = items
      .map((item) =>
        orderById.has(item.id)
          ? { ...item, display_order: orderById.get(item.id) }
          : item,
      )
      .sort(
        (a, b) =>
          typeOrder[a.partner_type] - typeOrder[b.partner_type] ||
          a.display_order - b.display_order ||
          a.name.localeCompare(b.name),
      );

    setItems(reorderedItems);
    setIsReordering(true);
    try {
      await apiClient.put("/partners/reorder", {
        orderedItems: reorderedGroup.map((item, index) => ({
          id: item.id,
          display_order: index + 1,
        })),
      });
      showSuccessToast(`${TYPE_LABELS[draggedItem.partner_type]} order updated successfully`);
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to update partner order");
      fetchPartners();
    } finally {
      setIsReordering(false);
      resetDragState();
    }
  };

  if (!canList) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Corporate, Insurance &amp; TPA Partners</h1>
        <p className="mt-2 text-sm text-slate-500">You do not have permission to view this module.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6" onDragOver={handleDragAutoScroll}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Corporate, Insurance &amp; TPA Partners</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Manage public partner names, logos, categories and visibility. Drag rows to reorder partners within the same group.</p>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="rounded-lg" onClick={fetchPartners}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          {canCreate && (
            <Button type="button" className="rounded-lg" onClick={() => navigate("/partners/new")}>
              <Plus className="mr-2 h-4 w-4" /> Add Partner
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {GROUPS.map(({ id, label, icon: Icon }) => {
          const isActive = activeGroup === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveGroup(id)}
              className={`flex items-center justify-between rounded-lg border p-4 text-left transition ${
                isActive
                  ? "border-primary bg-primary text-white shadow-lg shadow-primary/20"
                  : "border-slate-200 bg-white text-slate-700 hover:border-primary/30"
              }`}
            >
              <span className="flex items-center gap-3">
                <Icon className="h-5 w-5" />
                <span className="text-sm font-bold">{label}</span>
              </span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${isActive ? "bg-white/20" : "bg-slate-100"}`}>
                {counts[id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur-xl">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search partner or category"
              className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" aria-label="Filter partners by status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-white/60 bg-white/80 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-14 text-center text-sm font-medium text-slate-500">Loading partners...</div>
        ) : filteredItems.length === 0 ? (
          <div className="py-14 text-center text-sm text-slate-500">No partners match the selected filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="w-12 px-3 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500" aria-label="Reorder" />
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Partner</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Group</th>
                  <th className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredItems.map((item) => {
                  const logoUrl = resolvePartnerLogoUrl(item.logo_url);
                  return (
                    <tr
                      key={item.id}
                      draggable={canEdit && !isReordering}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", item.id);
                        setDraggedId(item.id);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        if (draggedId && draggedId !== item.id) setDropTargetId(item.id);
                      }}
                      onDrop={(event) => handleDrop(event, item)}
                      onDragEnd={resetDragState}
                      className={`transition ${
                        draggedId === item.id
                          ? "bg-primary/5 opacity-50"
                          : dropTargetId === item.id
                            ? "bg-blue-50 ring-2 ring-inset ring-primary/20"
                            : "hover:bg-slate-50/70"
                      } ${canEdit && !isReordering ? "cursor-grab active:cursor-grabbing" : ""}`}
                    >
                      <td className="px-3 py-4 text-slate-300">
                        <GripVertical className="h-5 w-5" aria-hidden="true" />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex min-w-[320px] items-center gap-4">
                          <span className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white p-2">
                            {logoUrl ? (
                              <img src={logoUrl} alt={`${item.name} logo`} className="h-full w-full object-contain" />
                            ) : (
                              <ImageIcon className="h-5 w-5 text-slate-300" />
                            )}
                          </span>
                          <div>
                            <p className="max-w-2xl font-semibold leading-6 text-slate-900">{item.name}</p>
                            {item.category && <p className="mt-1 text-xs font-medium text-slate-500">{item.category}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-600">{TYPE_LABELS[item.partner_type]}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${item.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {item.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          {canEdit && (
                            <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate(`/partners/${item.id}/edit`)}>
                              <Pencil className="mr-2 h-4 w-4" /> Edit
                            </Button>
                          )}
                          {canDelete && (
                            <DeleteConfirmationButton onConfirm={() => handleDelete(item)}>
                              <Button type="button" variant="outline" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </Button>
                            </DeleteConfirmationButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Partners;
